import { ReactNode, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { LineChart, Star, Scale, Headphones, TrendingUp, TrendingDown, Network } from "lucide-react";
import { toast } from "sonner";
import api, { apiError } from "@/lib/api";
import { Article } from "@/lib/types";
import { useAuth } from "@/context/AuthContext";
import { useWatchlist, WatchEntry } from "@/components/WatchButton";
import { COIN_META, track } from "./navConfig";
import { Micro, NavRow, PanelCTA, StoryRow, SkRow, GhostWord, LiveDot, CoinIcon, Pct, Spark, fmtPrice, fmtBig, FOCUS, REGION, delay } from "./primitives";
import { NavMenuAd } from "@/components/AdSlot";

const LIVE_COINS = ["BTC", "ETH", "SOL", "XRP", "DOGE", "ADA"];

const useQuickWatch = (symbol: string) => {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const { data } = useWatchlist();
  const [busy, setBusy] = useState(false);
  const watched = !!data?.watchlist?.some((w: WatchEntry) => w.symbol === symbol);

  const toggle = async (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (!user) { toast.error("Sign in to build your watchlist"); return; }
    if (busy) return;
    setBusy(true);
    try {
      if (watched) {
        await api.delete(`/account/watchlist/${symbol}`);
        toast.success(`${symbol} removed from your watchlist`);
      } else {
        await api.post("/account/watchlist", { symbol, news: true, price: true });
        toast.success(`${symbol} added to your watchlist`);
      }
      queryClient.invalidateQueries({ queryKey: ["watchlist"] });
      track("nav_click", { quick_watch: symbol, added: !watched });
    } catch (err) { toast.error(apiError(err)); } finally { setBusy(false); }
  };

  return { watched, busy, toggle };
};

const QuickWatchStar = ({ symbol }: { symbol: string }) => {
  const { watched, busy, toggle } = useQuickWatch(symbol);
  return (
    <button type="button" onClick={toggle} disabled={busy}
      data-testid={`markets-watch-${symbol}`}
      aria-label={watched ? `Remove ${symbol} from watchlist` : `Add ${symbol} to watchlist`}
      title={watched ? "Remove from watchlist" : "Add to watchlist"}
      className={`shrink-0 rounded-md p-1.5 hover:bg-white/[0.07] motion-safe:transition-[color,opacity,background-color] motion-safe:duration-200 ${FOCUS} ${
        watched
          ? "text-[#F59E0B] opacity-100"
          : "text-gray-500 hover:text-[#F59E0B] opacity-0 group-hover/coin:opacity-100 focus-visible:opacity-100"}`}>
      <Star className={`h-3.5 w-3.5 ${watched ? "fill-[#F59E0B]" : ""}`} />
    </button>
  );
};

const CoinRow = ({ c }: { c: any }) => {
  const { data: detail } = useQuery({
    queryKey: ["nav-spark", c.symbol],
    queryFn: async () => (await api.get(`/markets/${c.symbol}?range=7d`)).data,
    staleTime: 5 * 60 * 1000,
  });
  const chart: number[] = (detail?.chart || []).map((p: any) => p.price);
  const up = c.change_24h >= 0;
  return (
    <Link to={`/coin/${c.symbol}`} data-testid={`markets-coin-${c.symbol}`}
      onClick={() => track("nav_click", { coin: c.symbol })}
      className={`group/coin flex items-center gap-2.5 rounded-lg border border-transparent px-2 py-2 -mx-2 hover:bg-white/[0.05] hover:border-white/[0.08] transition-colors duration-200 ${FOCUS}`}>
      <CoinIcon symbol={c.symbol} />
      <span className="min-w-0">
        <span className="block text-[13px] font-semibold text-gray-200 group-hover/coin:text-white leading-tight transition-colors">
          {COIN_META[c.symbol]?.name || c.symbol}
        </span>
        <span className="block font-mono text-[10px] text-gray-600 uppercase">{c.symbol}</span>
      </span>
      <span className="ml-auto flex items-center gap-2.5">
        <Spark points={chart} up={up} />
        <span className="text-right">
          <span className="block font-mono text-[13px] text-white leading-tight">{fmtPrice(c.price)}</span>
          <Pct v={c.change_24h} />
        </span>
        <QuickWatchStar symbol={c.symbol} />
      </span>
    </Link>
  );
};

const Metric = ({ label, value, sub }: { label: string; value: string; sub?: ReactNode }) => (
  <div className="rounded-lg border border-white/[0.08] bg-white/[0.02] px-3 py-2.5">
    <p className="font-mono text-[9px] uppercase tracking-wider text-gray-500">{label}</p>
    <p className="font-mono text-base font-bold text-white mt-0.5">{value}</p>
    {sub && <div className="mt-1">{sub}</div>}
  </div>
);

const MOOD_COLOR = (v: number) => (v < 40 ? "#F97316" : v < 60 ? "#A3A3A3" : "#84CC16");

const MoodSpark = ({ history, color }: { history: { value: number }[]; color: string }) => {
  const vals = history.map((h) => h.value);
  const min = Math.min(...vals), max = Math.max(...vals);
  const span = max - min || 1;
  const W = 240, H = 26;
  const pts = vals.map((v, i) =>
    `${(i / (vals.length - 1)) * W},${H - 3 - ((v - min) / span) * (H - 6)}`).join(" ");
  return (
    <svg viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="none" aria-hidden
      data-testid="markets-mood-spark" className="w-full h-6 mt-2">
      <polyline pathLength={1} className="nl-spark-line" points={pts} fill="none" stroke={color}
        strokeWidth="1.5" strokeOpacity="0.75" strokeLinejoin="round" strokeLinecap="round" />
    </svg>
  );
};

export const MarketsMenu = () => {
  const { data: markets } = useQuery({
    queryKey: ["markets"],
    queryFn: async () => (await api.get("/markets")).data,
    staleTime: 90 * 1000,
  });
  const { data: global } = useQuery({
    queryKey: ["markets-global"],
    queryFn: async () => (await api.get("/markets/global")).data,
    staleTime: 10 * 60 * 1000,
  });
  const { data: li } = useQuery({
    queryKey: ["layer-index"],
    queryFn: async () => (await api.get("/layer-index")).data,
    staleTime: 5 * 60 * 1000,
  });
  const { data: news, isLoading: newsLoading } = useQuery({
    queryKey: ["nav-markets-news"],
    queryFn: async () => (await api.get("/articles", { params: { category: "markets", limit: 3 } })).data,
    staleTime: 2 * 60 * 1000,
  });

  const coins = (markets?.coins || []).filter((c: any) => LIVE_COINS.includes(c.symbol));
  const stories: Article[] = news?.items || [];
  const history: { date: string; value: number }[] = li?.history || [];
  const yesterday = history.length > 1 ? history[history.length - 2]?.value : null;
  const avg7 = history.length ? Math.round(history.slice(-7).reduce((s, h) => s + h.value, 0) / Math.min(7, history.length)) : null;
  const mcapUp = (global?.market_cap_change_24h_pct ?? 0) >= 0;
  const volRatio = global?.available && global.volume_24h_usd && global.total_market_cap_usd
    ? (global.volume_24h_usd / global.total_market_cap_usd) * 100 : null;

  return (
    <div data-testid="layernav-markets" className="relative grid grid-cols-2 xl:grid-cols-[1.2fr_0.85fr_1.35fr_0.7fr] gap-x-7 gap-y-6 p-7">
      <GhostWord word="Markets" />
      <div className={`min-w-0 ${REGION}`} style={delay(0)}>
        <Micro cyan className="mb-3.5">Crypto<LiveDot /></Micro>
        {coins.length > 0 ? (
          <div className="space-y-0.5">
            {coins.map((c: any) => <CoinRow key={c.symbol} c={c} />)}
          </div>
        ) : (
          <p className="font-mono text-[11px] text-gray-600 py-4">Market data temporarily unavailable</p>
        )}
      </div>

      <div className={REGION} style={delay(1)}>
        <Micro className="mb-3.5">Market Pulse</Micro>
        {global?.available ? (
          <div className="space-y-2" data-testid="markets-pulse">
            <Metric label="Total Market Cap" value={fmtBig(global.total_market_cap_usd)}
              sub={
                <span className="flex items-center gap-1.5">
                  {mcapUp
                    ? <TrendingUp className="h-3 w-3 text-[#22C55E]" />
                    : <TrendingDown className="h-3 w-3 text-[#EF4444]" />}
                  <Pct v={global.market_cap_change_24h_pct} />
                  <span className="font-mono text-[9px] text-gray-600 uppercase">24h</span>
                </span>
              } />
            <Metric label="BTC Dominance"
              value={global.btc_dominance ? `${global.btc_dominance.toFixed(1)}%` : "—"}
              sub={global.btc_dominance ? (
                <span className="block h-1 rounded-full bg-white/[0.07] overflow-hidden">
                  <span className="block h-full rounded-full bg-gradient-to-r from-[#F7931A] to-[#7C3AED]"
                    style={{ width: `${Math.min(100, global.btc_dominance)}%` }} />
                </span>
              ) : undefined} />
            <Metric label="24H Volume" value={fmtBig(global.volume_24h_usd)}
              sub={volRatio !== null ? (
                <span className="font-mono text-[10px] text-gray-500">≈ {volRatio.toFixed(1)}% of market cap</span>
              ) : undefined} />
          </div>
        ) : (
          <p className="font-mono text-[11px] text-gray-600 py-2" data-testid="markets-pulse-unavailable">
            Market data temporarily unavailable
          </p>
        )}
        {li && (
          <div className="rounded-lg border border-white/[0.08] bg-white/[0.02] px-3 py-2.5 mt-2" data-testid="markets-mood">
            <p className="font-mono text-[9px] uppercase tracking-wider text-gray-500 mb-1.5">Market Mood · Layer Index</p>
            <div className="grid grid-cols-3 gap-1 text-center">
              {[{ k: "Now", v: li.value }, { k: "Yesterday", v: yesterday }, { k: "7D Avg", v: avg7 }].map((m) => (
                <div key={m.k}>
                  <p className="font-mono text-sm font-bold" style={{ color: m.v != null ? MOOD_COLOR(m.v) : "#555" }}>
                    {m.v ?? "—"}
                  </p>
                  <p className="font-mono text-[8px] uppercase tracking-wider text-gray-600">{m.k}</p>
                </div>
              ))}
            </div>
            {history.length > 2 && (
              <MoodSpark history={history.slice(-14)} color={MOOD_COLOR(li.value ?? 50)} />
            )}
          </div>
        )}
      </div>

      <div className="col-span-2 grid grid-cols-2 xl:grid-cols-[1.35fr_0.7fr] gap-x-7 gap-y-5 content-start">
        <div className={`min-w-0 ${REGION}`} style={delay(2)}>
          <Micro cyan className="mb-3.5">Trending in Markets</Micro>
          <div className="space-y-1">
            {newsLoading && <><SkRow /><SkRow /><SkRow /></>}
            {!newsLoading && stories.map((a, i) => <StoryRow key={a.slug} a={a} testid={`markets-story-${i}`} />)}
            {!newsLoading && stories.length === 0 && <p className="text-sm text-gray-500">No market stories right now.</p>}
          </div>
          <div className="mt-2.5 pl-2">
            <PanelCTA to="/markets" testid="markets-view-news">View more market news</PanelCTA>
          </div>
        </div>

        <div className={REGION} style={delay(3)}>
          <Micro className="mb-3.5">Quick Access</Micro>
          <div className="space-y-1">
            <NavRow to="/coin/BTC" icon={LineChart} label="Live Charts" desc="BTC, ETH, SOL & more" testid="markets-qa-charts" />
            <NavRow to="/entities" icon={Network} label="Entities" desc="Protocol & token intelligence" testid="markets-qa-entities" />
            <NavRow to="/account" icon={Star} label="Watchlist" desc="Track your assets" testid="markets-qa-watchlist" />
            <NavRow to="/regulation-tracker" icon={Scale} label="Reg Tracker" desc="Bills, rulings & enforcement" testid="markets-qa-tracker" />
            <NavRow to="/briefs" icon={Headphones} label="Daily Brief" desc="The audio market brief" testid="markets-qa-brief" />
          </div>
        </div>

        <NavMenuAd className="col-span-2 mt-1" />
      </div>
    </div>
  );
};
